import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getHot, typeLabels } from "@/lib/catalog";

export default function HeroCarousel() {
  const slides = getHot(4);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const total = slides.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total],
  );

  useEffect(() => {
    if (paused) return;
    timerRef.current = window.setTimeout(() => go(index + 1), 6000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [index, paused, go]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(index - 1);
      if (e.key === "ArrowRight") go(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, go]);

  const active = slides[index];

  if (!active) return null;

  return (
    <section
      className="relative w-full h-[560px] md:h-[640px] lg:h-[700px] overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(diff) > 50) go(index + (diff < 0 ? 1 : -1));
        touchStartX.current = null;
      }}
      aria-label="热门影视轮播"
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== index}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.title} 剧照`}
            title={`${slide.title} 在线观看日韩影视`}
            className={`w-full h-full object-cover ${i === index ? "animate-soft-zoom" : ""}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/70 to-foreground-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/20 to-foreground-950/50" />

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-2xl">
          <div key={active.id} className="animate-fade-up">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-[11px] font-label font-bold tracking-wide">
                热度 {active.heat}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-background-50/15 backdrop-blur-sm text-background-50 text-[11px] font-label font-semibold">
                {typeLabels[active.type]}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-background-50/15 backdrop-blur-sm text-background-50 text-[11px] font-label font-semibold">
                {active.region} · {active.year}
              </span>
            </div>

            <h2 className="mt-4 font-heading text-3xl md:text-5xl lg:text-[54px] font-black text-background-50 leading-[1.12] tracking-tight">
              {active.title}
            </h2>
            <p className="mt-2 font-label text-xs md:text-sm tracking-[0.16em] uppercase text-background-50/50">
              {active.originalTitle}
            </p>
            <p className="mt-4 text-sm md:text-base text-background-100/85 max-w-xl leading-relaxed line-clamp-2 md:line-clamp-3">
              {active.synopsis}
            </p>

            <div className="mt-4 flex items-center gap-3 flex-wrap text-xs md:text-sm text-background-100/70">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400 w-3.5 h-3.5 flex items-center justify-center" />
                <span className="font-label font-semibold text-background-50">{active.rating.toFixed(1)}</span>
              </span>
              <span className="w-px h-3 bg-background-50/25" />
              <span>{active.genre.join(" / ")}</span>
              <span className="w-px h-3 bg-background-50/25" />
              <span>{active.episodes}</span>
            </div>

            <div className="mt-7 flex items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="inline-flex items-center gap-2 h-11 md:h-12 px-5 md:px-6 rounded-md bg-accent-500 text-background-50 text-sm font-label font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors"
              >
                <i className="ri-play-fill text-base w-4 h-4 flex items-center justify-center" />
                查看详情
              </Link>
              <Link
                to={`/category/${active.type}`}
                className="inline-flex items-center gap-2 h-11 md:h-12 px-5 md:px-6 rounded-md bg-background-50/10 backdrop-blur-sm border border-background-50/25 text-background-50 text-sm font-label font-semibold whitespace-nowrap hover:bg-background-50/20 transition-colors"
              >
                更多{typeLabels[active.type]}
                <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-8 md:bottom-10 left-4 md:left-8 right-4 md:right-8 flex items-end justify-between gap-4">
        <div className="flex items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到 ${slide.title}`}
              className={`h-1.5 rounded-full cursor-pointer transition-all duration-300 ${
                i === index ? "w-10 bg-accent-500" : "w-5 bg-background-50/35 hover:bg-background-50/60"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:block font-label text-[11px] tracking-[0.2em] text-background-50/60 mr-2">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="上一部"
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-50/30 text-background-50 flex items-center justify-center cursor-pointer hover:bg-background-50/15 transition-colors"
          >
            <i className="ri-arrow-left-s-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="下一部"
            className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-background-50/30 text-background-50 flex items-center justify-center cursor-pointer hover:bg-background-50/15 transition-colors"
          >
            <i className="ri-arrow-right-s-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
      </div>
    </section>
  );
}